import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Blogs.css";
import Footer from "./footer";

function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState("All");

  const blogsPerPage = 9;
  const navigate = useNavigate();
  const API_BASE = "https://api.niveshly.com";

  // ⭐ Generate slug from title (same as BlogDetail.js)
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  // ✅ Fetch blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_BASE}/blogs`);
        if (!response.ok) throw new Error("Failed to fetch blogs");
        const data = await response.json();
        setBlogs(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // ✅ Handle like click
  const handleLike = async (id, e) => {
    e.stopPropagation(); // Prevent card click

    const updatedBlogs = blogs.map((blog) =>
      blog.id === id ? { ...blog, likes: (blog.likes || 0) + 1 } : blog
    );
    setBlogs(updatedBlogs);

    try {
      await fetch(`${API_BASE}/blogs/${id}/like`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to update like:", error);
    }
  };

  // ✅ Filter blogs by search + keyword
  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (blog.tags &&
        blog.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        )) ||
      (blog.content &&
        blog.content.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesKeyword =
      selectedKeyword === "All" ||
      (blog.tags &&
        blog.tags.some(
          (tag) => tag.toLowerCase() === selectedKeyword.toLowerCase()
        ));

    return matchesSearch && matchesKeyword;
  });

  // ✅ Pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // ⭐ Handle blog click - Navigate with slug + ID query param
  const handleBlogClick = async (blog) => {
    try {
      await fetch(`${API_BASE}/blogs/${blog.id}/view`, { method: "POST" });
      setBlogs((prev) =>
        prev.map((b) =>
          b.id === blog.id ? { ...b, views: (b.views || 0) + 1 } : b
        )
      );
    } catch (err) {
      console.error("Failed to increment views:", err);
    }

    // Navigate with title slug and ID as query param
    const slug = generateSlug(blog.title);
    navigate(`/blog/${slug}?id=${blog.id}`);
  };

  // ✅ Handle Read Blog button click
  const handleReadBlog = (blog, e) => {
    e.stopPropagation(); // Prevent card click
    handleBlogClick(blog);
  };

  return (
    <div className="blogs-page">
      {/* Hero Section */}
      <section className="blogs-hero">
        <h1>Investment Insights & Market Trends</h1>
        <p>
          Stay informed with expert analysis on pre-IPO and unlisted equity
          markets
        </p>
      </section>

      {/* ✅ Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search Article by title, keyword, or tag..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="search-input"
        />
      </div>

      {/* ✅ Keyword Filter Buttons */}
      <div className="keyword-filter">
        {["All", "NSE", "MSEI", "TECHNOLOGY", "STARTUPS", "MARKET ANALYSIS", "IPO", "GROW",
          "HOTEL", "EDUCATION", "AGRICULTURE", "ELECTRONICS", "HEALTHCARE",
          "MANUFACTURING", "MINERALS", "FOOD & BEVERAGE", "STEEL", "INSURANCE", "HOSPITALITY",
          "INFRASTRUCTURE", "DEFENCE", "AUTOMOTIVE", "ENERGY", "IT", "FINANCE"
        ].map(
          (keyword) => (
            <button
              key={keyword}
              className={`keyword-btn ${selectedKeyword === keyword ? "active" : ""
                }`}
              onClick={() => {
                setSelectedKeyword(keyword);
                setCurrentPage(1);
              }}
            >
              {keyword}
            </button>
          )
        )}
      </div>

      {/* Blogs Grid */}
      <div className="blogs-container">
        {loading ? (
          <p className="loading-text">Loading Article...</p>
        ) : error ? (
          <p className="error-text">Error: {error}</p>
        ) : filteredBlogs.length === 0 ? (
          <p className="empty-text">No Article match your search.</p>
        ) : (
          <>
            <div className="blogs-grid">
              {currentBlogs.map((blog) => (
                <article
                  key={blog.id}
                  className="blog-card"
                  onClick={() => handleBlogClick(blog)}
                >
                  <div className="blog-image-wrapper">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="blog-image"
                    />
                    <button
                      className="share-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const slug = generateSlug(blog.title);
                        const url = `${window.location.origin}/blog/${slug}?id=${blog.id}`;
                        if (navigator.share) {
                          navigator.share({
                            title: blog.title,
                            text: blog.excerpt,
                            url: url,
                          });
                        } else {
                          navigator.clipboard.writeText(url);
                          alert("Link copied to clipboard!");
                        }
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                    </button>
                  </div>

                  <div className="blog-content">
                    <h2 className="blog-title">{blog.title}</h2>

                    {blog.tags && blog.tags.length > 0 && (
                      <div className="blog-tags">
                        {blog.tags.map((tag, index) => (
                          <span key={index} className="blog-tag">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* ✅ Likes & Views */}
                    <div className="blog-stats">
                      <button
                        className="like-btn"
                        onClick={(e) => handleLike(blog.id, e)}
                      >
                        ❤️ {blog.likes || 0}
                      </button>
                      <span className="views-count">👁 {blog.views || 0}</span>
                    </div>

                    {/* ⭐ Read Blog Button - Navigate to /blog/:slug?id=:id */}
                    <button
                      className="read-blog-btn"
                      onClick={(e) => handleReadBlog(blog, e)}
                    >
                      Read Article
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>

                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    className={`page-btn ${currentPage === index + 1 ? "active" : ""
                      }`}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  className="page-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA Section */}
      <section className="blogs-cta">
        <div className="cta-content">
          <h2>Ready to invest in high-growth opportunities?</h2>
          <p>
            Join now to invest in Pre-IPOs and high-growth private companies,
            instantly.
          </p>
        </div>
        <div>
          <a
            href="https://www.whatsapp.com/channel/0029VbAzMGIJENxxcshB5D0h"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              className="cta-btn"
              style={{ display: "flex", gap: "6px", alignItems: "center" }}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                alt="whatsapp"
                style={{ width: "18px", height: "18px" }}
              />
              WhatsApp
            </button>
          </a>
        </div>

      </section>

      <Footer />
    </div>
  );
}

export default Blogs;